import {useEffect} from 'react';
import {useNavigate} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {useFormContext} from 'react-hook-form';
import * as Sentry from '@sentry/react';
import {usePreloadLeaderData} from './usePreloadLeaderData';
import {useSchema} from 'context/guest';
import {useSummary} from '../../context/summary';
import {useOpenModals} from 'context/openModals';
import {useGetGuestId} from '../../hooks/useGetGuestId';
import {useValidatePreloadedFields} from './useValidatePreloadedFields';
import {
  useGuestFormFields,
  useModalControls,
  useGuest,
  usePreloadGuestData,
  useAsyncModalControls,
  usePath,
} from 'hooks';
import {PATHS} from 'Routes';
import {IS_LEADER_GUEST_STORAGE_KEY} from 'utils/constants';
import {SessionStorage} from '@guestapp/core';
import {FORM_NAMES, FormTypes} from '../../utils/guestForm';
import ModalSheet from '../common/ModalSheet';
import AutoFillModalSheet from '../ModalSheets/AutoFill';
import {FeedbackModal, shouldShowFeedbackModalToUser} from 'components/FeedbackModal';
import {PrintingCardsModal} from 'components/Kiosko';
import ChekinProSettings from '../../ChekinProSettings';
import {
  Fields,
  FormWrapper,
  LoadingContainer,
  LoadingText,
  MainStyledLoader,
} from './styled';
import {useDefaultContactData} from 'hooks/iv/useDefaultContactData';

type GuestFormFieldsProps = {
  setErrorModal: (value: boolean) => void;
};

function GuestFormFields({setErrorModal}: GuestFormFieldsProps) {
  const {reservationId} = useSummary();
  const {t} = useTranslation();
  const {customFormsVersion} = useSchema();
  const guestId = useGetGuestId();
  const {guest} = useGuest({id: guestId});
  const {countryCode} = useSummary();
  const navigate = useNavigate();
  const {getFullPath} = usePath();
  const {
    isOpen: isFeedbackModalOpen,
    openModal: openFeedbackModal,
    closeModal: closeFeedbackModal,
  } = useAsyncModalControls<void>();

  const {
    isLoadingForm,
    isLoadedForm,
    FormModals,
    renderFormFields,
    hasSignature,
    SignatureField,
    isDataIncompleteModalOpen,
  } = useGuestFormFields({
    id: guestId,
    housingCountry: countryCode,
    onGuestRegistered: async data => {
      if (!ChekinProSettings.isSDK && shouldShowFeedbackModalToUser(data)) {
        await openFeedbackModal().catch(error => {
          Sentry.captureException(error);
          console.warn('Feedback modal error:', error);
        });
      }
    },
  });
  const {getValues, setValue} = useFormContext<FormTypes>();
  const {setIsFormTouched} = useOpenModals();
  const {
    isOpen: isAutoFillModalOpen,
    openModal: openAutoFillModal,
    closeModal: closeAutoFillModal,
  } = useModalControls();

  const {preloadPersistedFormData} = usePreloadGuestData({
    onPreloaded: openAutoFillModal,
    guestData: guest,
  });

  const {email, phone, isLeaderGuest} = useDefaultContactData();

  useEffect(() => {
    if (isLeaderGuest) {
      const values = getValues();
      if (email && !values[FORM_NAMES.email]) {
        setValue(FORM_NAMES.email, email);
      }
      if (phone && !values[FORM_NAMES.phone]) {
        setValue(FORM_NAMES.phone, phone);
      }
    }
  }, [getValues, setValue, email, phone, isLeaderGuest]);

  useValidatePreloadedFields();

  usePreloadLeaderData({countryCode});

  useEffect(() => {
    if (guest) {
      SessionStorage.set(IS_LEADER_GUEST_STORAGE_KEY, guest?.is_leader);
    }
  }, [guest]);

  useEffect(() => {
    setErrorModal(isDataIncompleteModalOpen);
    if (isLoadedForm) {
      preloadPersistedFormData(guestId);
      setIsFormTouched(Boolean(Object.values(getValues()).length));
    }
  }, [
    getValues,
    isLoadedForm,
    preloadPersistedFormData,
    setIsFormTouched,
    isDataIncompleteModalOpen,
    setErrorModal,
    guestId,
  ]);

  return (
    <>
      {isLoadingForm ? (
        <LoadingContainer>
          <MainStyledLoader visible width={100} />
          <LoadingText>{`${t('loading_form')}...`}</LoadingText>
        </LoadingContainer>
      ) : (
        <FormWrapper className="guest-form-wrapper">
          <Fields
            className="guest-form-fields"
            $rowsCount={1}
            $hasSignature={hasSignature}
            $customFormsVersion={customFormsVersion}
          >
            {renderFormFields()}
          </Fields>
          {SignatureField}
          {FormModals()}
        </FormWrapper>
      )}
      <ModalSheet
        isOpenModalSheet={isAutoFillModalOpen}
        handleCloseModal={closeAutoFillModal}
        withCloseButton
        closeButtonType="click"
      >
        <AutoFillModalSheet onClose={closeAutoFillModal} />
      </ModalSheet>
      <PrintingCardsModal
        onMaybeLater={() => navigate(getFullPath(PATHS.remoteKeys))}
        onGoToKeyCards={() => navigate(getFullPath(PATHS.remoteKeys))}
      />
      <FeedbackModal
        reservationId={reservationId}
        isOpenModalSheet={isFeedbackModalOpen}
        onClose={closeFeedbackModal}
      />
    </>
  );
}

export {GuestFormFields};
